import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Switch from "../../components/Switch";
import SelectDropdown from "../SelectDropdown";
import { useAuth } from "../../providers/AuthContext";
import { getConfirmedRemoteParties } from "../../api/RemotePartiesOCPI";
import { FormCard } from "../FormCard";
import { DetailRow } from "../Rows";
import ParkingSpace from "./ParkingSpace";
import RelatedLocationsEditor from "./RelatedLocationsEditor";
import HoursEditor from "./HoursEditor";
import HeavyVehicleEditor from "../HeavyVehicleEditor";

export default function LocationData({
  openingTimes,
  setOpeningTimes,
  setHasChanges,
  chargingSite,
  setChargingSite,
  parties,
  setParties,
  parkingSpaces,
  setParkingSpaces,
  invalidParkingVehicleTypesIndices = [],
  onClearParkingVehicleTypesInvalid,
  invalidParkingPhysicalReferenceIndices = [],
  onClearParkingPhysicalReferenceInvalid,
  relatedLocations,
  setRelatedLocations,
  heavyVehicleData,
  setHeavyVehicleData,
  ocpiEditable = true,
}) {
  const { t } = useTranslation();
  const { getAccessToken } = useAuth();
  const [availableParties, setAvailableParties] = useState([]);

  useEffect(() => {
    if (!ocpiEditable) {
      setAvailableParties([]);
      return;
    }

    const handleGetRemoteParties = async () => {
      try {
        const accessToken = await getAccessToken();
        const remoteParties = await getConfirmedRemoteParties(accessToken);
        setAvailableParties(remoteParties);
      } catch (error) {
        console.error("Failed to fetch remote parties", error);
      }
    };

    handleGetRemoteParties();
  }, [getAccessToken, ocpiEditable]);

  const selectedParties = availableParties
    .filter((party) =>
      parties.some(
        (selectedParty) =>
          !selectedParty.isDeleted &&
          selectedParty.remotePartyPublicId === party.remotePartyPublicId
      )
    )
    .map((party) => ({
      value: party,
      label: party.remotePartyName,
    }));

  const activePartyNames = parties
    .filter((party) => !party.isDeleted)
    .map((party) => party.remotePartyName)
    .filter(Boolean);

  const handlePartiesChange = (selectedOptions) => {
    const nextSelectedIds = new Set(
      (selectedOptions || []).map((option) => option.value.remotePartyPublicId)
    );

    const restoredOrExistingParties = parties.map((party) => ({
      ...party,
      isDeleted: !nextSelectedIds.has(party.remotePartyPublicId),
    }));

    const existingIds = new Set(
      restoredOrExistingParties.map((party) => party.remotePartyPublicId)
    );

    const newlyAddedParties = (selectedOptions || [])
      .map((option) => option.value)
      .filter((party) => !existingIds.has(party.remotePartyPublicId))
      .map((party) => ({ ...party, isNew: true }));

    setParties([...restoredOrExistingParties, ...newlyAddedParties]);
    setHasChanges(true);
  };

  return (
    <div className="flex flex-col gap-8">
      <FormCard
        title={t("site.location.publishTitle")}
        description={t("site.location.publishDescription")}
      >
        {ocpiEditable ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            <div className="space-y-2">
              <span className="text-sm text-mute-700 font-normal">
                {t("site.location.publishOnOcpi")}
              </span>
              <Switch
                checked={chargingSite.isPublished}
                onChange={(e) => {
                  setChargingSite({ ...chargingSite, isPublished: e });
                  setHasChanges(true);
                }}
              />
            </div>

            <div className="space-y-2">
              {chargingSite.isPublished && (
                <>
                  <span className="text-sm text-mute-700 font-normal">
                    {t("site.location.remoteParties")}
                  </span>
                  <SelectDropdown
                    options={availableParties.map((party) => ({
                      value: party,
                      label: party.remotePartyName,
                    }))}
                    selectedValues={selectedParties}
                    onChange={handlePartiesChange}
                    placeholder={t("site.location.selectRemoteParties")}
                    label=""
                  />
                  <p className="text-sm text-mute-500">
                    {t("site.location.selectRemotePartiesHelp")}
                  </p>
                </>
              )}
            </div>
          </div>
        ) : (
          <>
            <DetailRow
              label={t("site.location.publishOnOcpi")}
              value={chargingSite.isPublished ? t("common.yes") : t("common.no")}
            />
            <DetailRow
              label={t("site.location.remoteParties")}
              value={
                chargingSite.isPublished
                  ? activePartyNames.length > 0
                    ? activePartyNames.join(", ")
                    : "-"
                  : "-"
              }
            />
          </>
        )}
      </FormCard>

      <FormCard
        title={t("site.location.publishPublicInAppTitle")}
        description={t("site.location.publishPublicInAppDescription")}
      >
        {ocpiEditable ? (
          <div className="space-y-2">
            <span className="text-sm text-mute-700 font-normal">
              {t("site.location.publishPublicInApp")}
            </span>
            <Switch
              checked={!!chargingSite.publishPublicInApp}
              onChange={(e) => {
                setChargingSite({ ...chargingSite, publishPublicInApp: e });
                setHasChanges(true);
              }}
            />
          </div>
        ) : (
          <DetailRow
            label={t("site.location.publishPublicInApp")}
            value={chargingSite.publishPublicInApp ? t("common.yes") : t("common.no")}
          />
        )}
      </FormCard>

      <HeavyVehicleEditor
        data={heavyVehicleData}
        onChange={setHeavyVehicleData}
      />

      <ParkingSpace
        data={parkingSpaces}
        onChange={setParkingSpaces}
        invalidVehicleTypesIndices={invalidParkingVehicleTypesIndices}
        onClearVehicleTypesInvalid={onClearParkingVehicleTypesInvalid}
        invalidPhysicalReferenceIndices={invalidParkingPhysicalReferenceIndices}
        onClearPhysicalReferenceInvalid={onClearParkingPhysicalReferenceInvalid}
      />

      <RelatedLocationsEditor
        data={relatedLocations}
        onChange={setRelatedLocations}
       />

        <HoursEditor
            openingTimes={openingTimes}
            setOpeningTimes={setOpeningTimes}
            setHasChanges={setHasChanges}
        />

    </div>
  );
}
